import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetAuthApi, usePutAuthApi } from './UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';

const PREFERENCES_URL = 'users/preferences';

type Preferences = Record<string, string>;

const readLocal = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeLocal = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Private mode and quota failures must not break the screen.
  }
};

/**
 * A small choice the user made about how the site looks to them, stored on their
 * account so it follows them to another browser or machine.
 *
 * The server is the record, but it is not on the critical path for the first
 * paint: the last known answer is mirrored into localStorage and used
 * immediately, and the server's answer replaces it once it arrives. Without
 * that, every load would start with a menu in the wrong state and visibly
 * correct itself.
 *
 * Once the user changes the preference in this session, their action holds —
 * a late or refetched server response must not undo something they just did.
 */
const useUserPreference = (key: string): [string | null, (value: string | null) => void] => {
  const { currentUser } = useAuthContext();
  // An empty key is how a caller says "this screen has no preference to keep",
  // so the hook can still be called unconditionally.
  const isSignedIn = Boolean(currentUser?.token) && key !== '';
  const { data } = useGetAuthApi<Preferences>(isSignedIn ? PREFERENCES_URL : '');
  const { mutate } = usePutAuthApi<Preferences, object, Record<string, string | null>>(PREFERENCES_URL);

  const [value, setValue] = useState<string | null>(() => (key === '' ? null : readLocal(key)));
  const isUserEdited = useRef(false);

  const serverValue = data ? (data[key] ?? null) : undefined;

  useEffect(() => {
    if (isUserEdited.current || serverValue === undefined) return;
    setValue(serverValue);
    writeLocal(key, serverValue);
  }, [serverValue, key]);

  const set = useCallback(
    (next: string | null) => {
      if (key === '') return;
      isUserEdited.current = true;
      setValue(next);
      writeLocal(key, next);
      if (isSignedIn) {
        // A failure here costs the user nothing today — the local copy already
        // holds their choice — and the next screen they open will save again.
        mutate({ body: { [key]: next } });
      }
    },
    [key, isSignedIn, mutate]
  );

  return [value, set];
};

export default useUserPreference;
